import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ImagePlus,
  Loader2,
  Save,
  Upload,
  XCircle,
} from 'lucide-react';
import AdminLayout from './AdminLayout';
import {
  type AdMode,
  type AdsConfig,
  getAds,
  saveAds,
  uploadImage,
} from './api';

type Toast =
  | { kind: 'success'; message: string }
  | { kind: 'error'; message: string };

const MODE_LABEL: Record<AdMode, string> = {
  off: 'Off',
  admob: 'AdMob',
  custom: 'Custom image',
};

const MODE_DESCRIPTION: Record<AdMode, string> = {
  off: 'Hide the banner from every user.',
  admob: 'Serve Google AdMob banners using the IDs from the mobile .env.',
  custom: 'Show your own image with a tap-through URL.',
};

const DEFAULT_FORM: AdsConfig = {
  mode: 'admob',
  imageUrl: null,
  linkUrl: null,
  caption: null,
};

export default function AdsEditor() {
  const [form, setForm] = useState<AdsConfig>(DEFAULT_FORM);
  const [live, setLive] = useState<AdsConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Initial load
  useEffect(() => {
    let cancelled = false;
    getAds()
      .then((doc) => {
        if (cancelled) return;
        const merged: AdsConfig = {
          mode: (doc.mode as AdMode | undefined) ?? 'admob',
          imageUrl: doc.imageUrl ?? null,
          linkUrl: doc.linkUrl ?? null,
          caption: doc.caption ?? null,
        };
        setForm(merged);
        setLive(merged);
      })
      .catch(() => setToast({ kind: 'error', message: 'Failed to load config.' }))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-dismiss the toast after 4s so it doesn't pile up.
  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(id);
  }, [toast]);

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(live), [form, live]);

  async function handleFile(file: File) {
    setUploading(true);
    try {
      const { url } = await uploadImage(file);
      setForm((f) => ({ ...f, imageUrl: url }));
      setToast({ kind: 'success', message: 'Image uploaded.' });
    } catch {
      setToast({ kind: 'error', message: 'Upload failed. Try a PNG/JPG/WebP under 5 MB.' });
    } finally {
      setUploading(false);
    }
  }

  async function handleSave() {
    if (form.mode === 'off') {
      const ok = window.confirm(
        'This will hide the in-app ad banner from every user. Continue?',
      );
      if (!ok) return;
    }
    if (form.mode === 'custom') {
      if (!form.imageUrl) {
        setToast({ kind: 'error', message: 'Image is required in Custom mode.' });
        return;
      }
      if (!form.linkUrl) {
        setToast({ kind: 'error', message: 'Tap-through link is required in Custom mode.' });
        return;
      }
    }
    setSaving(true);
    try {
      const payload: AdsConfig = {
        mode: form.mode,
        imageUrl: form.mode === 'custom' ? form.imageUrl : null,
        linkUrl: form.mode === 'custom' ? form.linkUrl : null,
        caption: form.mode === 'custom' ? form.caption || null : null,
      };
      await saveAds(payload);
      setLive(payload);
      setForm(payload);
      setToast({ kind: 'success', message: 'Saved. Live on every device.' });
    } catch {
      setToast({ kind: 'error', message: 'Save failed. Check the inputs.' });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <AdminLayout title="Ad banner">
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  const liveModeLabel = live ? MODE_LABEL[live.mode] : '—';

  return (
    <AdminLayout title="Ad banner">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-2xl font-bold text-white">In-app ad banner</h2>
        <span className="pill">
          <span
            className={`h-1.5 w-1.5 rounded-full ${
              live?.mode === 'off' ? 'bg-slate-500' : 'bg-emerald-400'
            }`}
          />
          Currently live: <span className="font-bold text-white">{liveModeLabel}</span>
        </span>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-5">
        {/* Editor */}
        <section className="lg:col-span-3 space-y-8">
          {/* Mode picker */}
          <div className="glass-card p-5">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
              Mode
            </p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              {(['off', 'admob', 'custom'] as AdMode[]).map((m) => {
                const active = form.mode === m;
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, mode: m }))}
                    className={`rounded-xl border px-4 py-3 text-left transition-colors ${
                      active
                        ? 'border-brand-cyan bg-brand-cyan/10 text-white'
                        : 'border-white/10 bg-white/[0.02] text-slate-300 hover:border-white/20'
                    }`}
                  >
                    <p className="text-sm font-bold">{MODE_LABEL[m]}</p>
                    <p className="mt-1 text-xs text-slate-400">{MODE_DESCRIPTION[m]}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {form.mode === 'custom' && (
            <>
              {/* Image */}
              <div className="glass-card p-5">
                <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Banner image
                </p>

                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void handleFile(f);
                    if (fileRef.current) fileRef.current.value = '';
                  }}
                />

                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="inline-flex items-center gap-2 rounded-xl border border-brand-cyan/40 bg-brand-cyan/10 px-4 py-2.5 text-sm font-semibold text-brand-cyan hover:bg-brand-cyan/20 disabled:opacity-50"
                  >
                    {uploading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    {form.imageUrl ? 'Replace image' : 'Upload image'}
                  </button>
                  {form.imageUrl && (
                    <button
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, imageUrl: null }))}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-slate-300 hover:bg-white/10"
                    >
                      <XCircle className="h-4 w-4" />
                      Clear
                    </button>
                  )}
                </div>

                <p className="mt-3 text-xs text-slate-500">
                  PNG, JPG, or WebP up to 5 MB. The banner renders at the image's
                  natural aspect ratio across the full screen width.
                </p>

                <div className="mt-5">
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                    or paste an image URL
                  </label>
                  <input
                    type="url"
                    value={form.imageUrl ?? ''}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, imageUrl: e.target.value || null }))
                    }
                    placeholder="https://example.com/banner.png"
                    className="w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-brand-cyan focus:outline-none focus:ring-2 focus:ring-brand-cyan/30"
                  />
                </div>
              </div>

              {/* Link + caption */}
              <div className="glass-card p-5">
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Tap-through link <span className="text-red-400">*</span>
                </label>
                <input
                  type="url"
                  value={form.linkUrl ?? ''}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, linkUrl: e.target.value || null }))
                  }
                  placeholder="https://t.me/dinarpulse"
                  className="mb-5 w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-brand-cyan focus:outline-none focus:ring-2 focus:ring-brand-cyan/30"
                />

                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Caption (optional)
                </label>
                <input
                  type="text"
                  maxLength={160}
                  value={form.caption ?? ''}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, caption: e.target.value || null }))
                  }
                  placeholder="Join us on Telegram"
                  className="w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-brand-cyan focus:outline-none focus:ring-2 focus:ring-brand-cyan/30"
                />
                <p className="mt-1 text-xs text-slate-500">
                  {(form.caption ?? '').length}/160
                </p>
              </div>
            </>
          )}

          {/* Save bar */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleSave}
              disabled={!dirty || saving}
              className="inline-flex items-center gap-2 rounded-xl bg-brand-cyan px-5 py-3 text-sm font-bold text-brand-ink transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            {dirty && (
              <button
                type="button"
                onClick={() => live && setForm(live)}
                className="text-sm text-slate-400 hover:text-white"
              >
                Discard
              </button>
            )}
          </div>
        </section>

        {/* Live preview */}
        <section className="lg:col-span-2">
          <div className="glass-card p-5">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
              Preview
            </p>
            <Preview form={form} />
            <p className="mt-3 text-xs text-slate-500">
              Mirrors how the banner renders on mobile inside the app.
            </p>
          </div>
        </section>
      </div>

      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-xl border px-4 py-3 text-sm shadow-glow ${
            toast.kind === 'success'
              ? 'border-emerald-400/40 bg-emerald-400/10 text-emerald-200'
              : 'border-red-400/40 bg-red-400/10 text-red-200'
          }`}
        >
          {toast.kind === 'success' ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <AlertTriangle className="h-4 w-4" />
          )}
          {toast.message}
        </div>
      )}
    </AdminLayout>
  );
}

function Preview({ form }: { form: AdsConfig }) {
  if (form.mode === 'off') {
    return (
      <div className="rounded-xl border border-dashed border-white/10 bg-black/30 p-6 text-center text-sm text-slate-500">
        Banner is hidden.
      </div>
    );
  }
  if (form.mode === 'admob') {
    return (
      <div className="flex h-16 items-center justify-center rounded-xl border border-dashed border-white/10 bg-black/30 text-xs text-slate-500">
        AdMob banner (rendered by Google SDK on device)
      </div>
    );
  }
  // custom
  return (
    <div className="rounded-xl border border-white/10 bg-black/40 p-3">
      {form.imageUrl ? (
        <img
          src={form.imageUrl}
          alt="Custom ad preview"
          className="w-full rounded-lg object-contain"
        />
      ) : (
        <div className="flex h-32 items-center justify-center rounded-lg border border-dashed border-white/10 text-xs text-slate-500">
          <ImagePlus className="mr-2 h-4 w-4" /> No image selected
        </div>
      )}
      {form.caption && (
        <p className="mt-2 text-center text-xs text-slate-400">{form.caption}</p>
      )}
    </div>
  );
}
